import noop from 'lodash/noop';
import { v4 as uuid } from 'uuid';

/**
 * Generates a unique tab ID for the current browsing context.
 * This ID persists for the lifetime of the tab/window and is used
 * to stamp files with their creating tab for ownership tracking [ENG-3289].
 *
 * On first call, also acquires a long-lived Web Lock (`tab:{tabId}`)
 * as an "alive" signal. Other tabs probe this lock to detect dead tabs.
 */
let currentTabId: string | undefined;

export const getTabId = (): string => {
  if (!currentTabId) {
    currentTabId = uuid();
    holdTabAliveLock(currentTabId);
  }
  return currentTabId;
};

/**
 * Acquires a long-lived Web Lock that is held for the lifetime of the tab.
 * When the tab closes or crashes, the lock is automatically released,
 * allowing other tabs to detect that this tab is dead and recover its files.
 */
function holdTabAliveLock(tabId: string): void {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return;
  }

  // Request a lock and hold it indefinitely via a never-resolving promise.
  // The lock is automatically released when the tab closes/crashes.
  navigator.locks.request(`tab:${tabId}`, () => new Promise(noop)).catch(noop);
}
